import { dxtToLocalServerTime, getMomentTz } from "@dexteel/mesf-core";
import { useEffect, useMemo, useRef } from "react";
import { Asset, Task } from "../../../../controls/ganttAsset";
import type { ProcessLine } from "../../../../controls/ganttAsset/types/public-types";
import type { Process, ProcessEvent } from "../../models/ProcessGanttChart";
import {
  getColorForAsset,
  getColorForHeat,
  heatNameToColor,
} from "../utils/get-color-helpers";
import React from "react";
const moment = getMomentTz();

const ASSET_IDS = {
  EAF: 11,
  LMF: 12,
  CASTER: 13,
};

const DEFAULT_COLOR = "#e9ecef";

const FIXED_ASSETS: Asset[] = [
  {
    id: ASSET_IDS.EAF.toString(),
    name: "EAF",
    processes: [],
    processLines: [],
    color: getColorForAsset("EAF"),
    rowHeight: 150,
    barFill: 50,
    lineSectionFill: 40,
    lineFill: 20,
  },
  {
    id: ASSET_IDS.LMF.toString(),
    name: "LMF",
    processes: [],
    processLines: [],
    color: getColorForAsset("LMF"),
    rowHeight: 150,
    barFill: 50,
    lineSectionFill: 40,
    lineFill: 20,
  },
  {
    id: ASSET_IDS.CASTER.toString(),
    name: "Caster",
    processes: [],
    processLines: [],
    color: getColorForAsset("CASTER"),
    rowHeight: 250,
    barFill: 30,
    lineSectionFill: 60,
    lineFill: 10,
  },
];

//Processes
const createTask = (curr: Process, color: string): Task => ({
  id: `${curr.AssetId} - ${curr.HeatName}`,
  start: curr.Start,
  end: curr.End,
  type: "task",
  progress: 100,
  name: curr.HeatName ?? "Unlinked",
  source: curr,
  styles: {
    backgroundColor: color,
    backgroundSelectedColor: color,
    progressColor: color,
    progressSelectedColor: color,
    borderColor: "black",
  },
  textLeftBottom: curr.GradeName ?? "",
  textRightBottom: dxtToLocalServerTime(curr.End, "HH:mm"),
  textLeftTop: dxtToLocalServerTime(curr.Start, "HH:mm"),
  textRightTop: curr.LadleName ?? "",
});

//Process Lines
const formatDelay = (delayString: string) => {
  const [minutes, seconds] = delayString.split("m ");
  return `${minutes}min ${seconds}`;
};

const formatCommentLines = (
  comment: string,
  maxLength: number,
  prefix: string = "",
) => {
  if (!comment) return [];

  const words = comment.split(" ");
  const lines: string[] = [];
  let currentLine = "";

  words.forEach((word) => {
    const isFirstLine = lines.length === 0;
    const effectivePrefix = isFirstLine && prefix ? prefix : "";
    const totalLength =
      effectivePrefix.length +
      currentLine.length +
      (currentLine ? 1 : 0) +
      word.length;

    if (totalLength <= maxLength) {
      currentLine += (currentLine ? " " : "") + word;
    } else {
      if (isFirstLine && prefix) {
        lines.push(prefix + currentLine);
      } else {
        lines.push(currentLine);
      }
      currentLine = word;
    }
  });

  if (currentLine) {
    const isFirstLine = lines.length === 0;
    if (isFirstLine && prefix) {
      lines.push(prefix + currentLine);
    } else {
      lines.push(currentLine);
    }
  }

  return lines;
};

const createProcessLine = (
  event: ProcessEvent,
  strandCode?: string,
): ProcessLine => ({
  id: strandCode ? `${event.AssetId}-${strandCode}` : event.AssetId.toString(),
  DelayId: event.DelayId,
  name: "",
  eventCode: event.EventCode,
  DelayCodeName: event.DelayCodeName,
  start: event.Start,
  end: event.End,
  type: "taskLine" as const,
  comments: (
    <>
      {event.EventCode === "D" && (
        <>
          <p>
            <strong>Duration: {formatDelay(event.Duration)}</strong>
          </p>
          <p>Delay Code: {event.DelayCodeName || "(Not specified)"}</p>
          <div style={{ whiteSpace: "pre-line" }}>
            {event.Comments
              ? formatCommentLines(event.Comments, 60, "Comments: ").map(
                  (line, i) => (
                    <React.Fragment key={i}>
                      {line}
                      <br />
                    </React.Fragment>
                  ),
                )
              : "Comments: (Not specified)"}
          </div>
        </>
      )}
      {event.EventCode === "H" && (
        <p>
          <strong>Energy Cons: {event.Comments}</strong>
        </p>
      )}
      {event.EventCode !== "D" && event.EventCode !== "H" && (
        <>
          <p>
            <strong>Strand #{event.EventCode}</strong>
          </p>
          <p>Speed: {event.Comments?.replace(/[^\d.]/g, "")} ipm</p>
        </>
      )}
      <p>
        {`From: ${dxtToLocalServerTime(
          event.Start,
          "HH:mm:ss",
        )} to: ${dxtToLocalServerTime(event.End, "HH:mm:ss")}`}
      </p>
    </>
  ),
  styles: {
    backgroundColor: event.Color || "blue",
    progressColor: event.Color || "blue",
    borderWidth: 1,
    borderColor: "#707175",
  },
});

//Process line without data
const createDefaultProcessLine = (
  assetId: number,
  code: string,
  currentStart: Date | null,
) => ({
  id: `${assetId}-${code}-default`,
  name: "",
  eventCode: code,
  start: currentStart || new Date(),
  end: moment(currentStart).add(1, "second").toDate(),
  type: "taskLine" as const,
  comments: "",
  styles: {
    backgroundColor: "rgba(0, 0, 0, 0)",
    progressColor: "rgba(0, 0, 0, 0)",
    borderWidth: 1,
    borderColor: "#707175",
  },
});

const handleEAFProcesses = (
  assetId: number,
  processEvents: ProcessEvent[],
  currentStart: Date | null,
): ProcessLine[] => {
  const processLines: ProcessLine[] = [
    createDefaultProcessLine(assetId, "", currentStart),
  ];
  for (const line of ["Heatings", "Delays"]) {
    const events = processEvents.filter((e) => e.EventCode === line[0]);
    if (events.length > 0) {
      for (const event of events) processLines.push(createProcessLine(event));
    } else {
      processLines.push(
        createDefaultProcessLine(assetId, line[0], currentStart),
      );
    }
  }
  return processLines;
};

const handleLMFProcesses = (
  assetId: number,
  processEvents: ProcessEvent[],
  currentStart: Date | null,
): ProcessLine[] => {
  const processLines: ProcessLine[] = [
    createDefaultProcessLine(assetId, "", currentStart),
  ];
  for (const line of ["Heatings"]) {
    const events = processEvents.filter((e) => e.EventCode === line[0]);
    if (events.length > 0) {
      for (const event of events) processLines.push(createProcessLine(event));
    } else {
      processLines.push(
        createDefaultProcessLine(assetId, line[0], currentStart),
      );
    }
  }
  return processLines;
};

const handleCasterProcesses = (
  assetId: number,
  processEvents: ProcessEvent[],
  currentStart: Date | null,
): ProcessLine[] => {
  const processLines: ProcessLine[] = [];
  for (const line of ["Delays"]) {
    const events = processEvents.filter((e) => e.EventCode === line[0]);
    if (events.length > 0) {
      for (const event of events) processLines.push(createProcessLine(event));
    } else {
      processLines.push(
        createDefaultProcessLine(assetId, line[0], currentStart),
      );
    }
  }

  for (const strandCode of ["1", "2", "3", "4", "5", ""]) {
    const events = processEvents.filter((e) => e.EventCode === strandCode);
    if (events.length > 0) {
      for (const event of events)
        processLines.push(createProcessLine(event, strandCode));
    } else {
      processLines.push(
        createDefaultProcessLine(ASSET_IDS.CASTER, strandCode, currentStart),
      );
    }
  }
  return processLines;
};

/**
 * Transforms raw gantt query data into Asset[] for the Gantt chart component.
 * Pure declarative hook — receives data from TanStack Query, returns memoized assets.
 */
export const useGanttUpdater = (
  currentStart: Date | null,
  rawProcesses: Process[] | undefined,
  rawProcessEvents: ProcessEvent[] | undefined,
): Asset[] => {
  const pendingColorSync = useRef<Map<string, string> | null>(null);

  const assets = useMemo(() => {
    if (!rawProcesses || !rawProcessEvents) return FIXED_ASSETS;

    const processes = rawProcesses.map((row) => ({
      ...row,
      Start: moment.utc(row.Start).toDate(),
      EAFStartProcess: moment.utc(row.EAFStartProcess).toDate(),
      End: moment.utc(row.End).toDate(),
    }));
    const processEvents = rawProcessEvents.map((row) => ({
      ...row,
      Start: moment.utc(row.Start).toDate(),
      End: moment.utc(row.End).toDate(),
    }));

    const updatedAssets: Asset[] = FIXED_ASSETS.map((asset) => ({
      ...asset,
      processes: [],
      processLines: [],
    }));

    const processEventsByAsset = new Map<number, ProcessEvent[]>();
    for (const event of processEvents) {
      const assetEvents = processEventsByAsset.get(event.AssetId) ?? [];
      assetEvents.push(event);
      processEventsByAsset.set(event.AssetId, assetEvents);
    }

    // Build a local color map for this render; read from the persistent cache but never mutate it inside useMemo
    const localColorMap = new Map(heatNameToColor);

    for (const curr of processes) {
      const assetIndex = updatedAssets.findIndex(
        (a) => Number(a.id) === curr.AssetId,
      );
      if (assetIndex === -1) continue;

      const color = curr.HeatName
        ? localColorMap.get(curr.HeatName) || getColorForHeat(curr.HeatName)
        : DEFAULT_COLOR;

      if (curr.HeatName) localColorMap.set(curr.HeatName, color);

      const task = createTask(curr, color);
      updatedAssets[assetIndex].processes.push(task);
    }

    for (const asset of updatedAssets) {
      const assetId = Number(asset.id);
      const assetEvents = processEventsByAsset.get(assetId) ?? [];

      if (assetId === ASSET_IDS.EAF) {
        asset.processLines = handleEAFProcesses(
          assetId,
          assetEvents,
          currentStart,
        );
      } else if (assetId === ASSET_IDS.LMF) {
        asset.processLines = handleLMFProcesses(
          assetId,
          assetEvents,
          currentStart,
        );
      } else {
        asset.processLines = handleCasterProcesses(
          assetId,
          assetEvents,
          currentStart,
        );
      }
    }

    // Stage new colors for sync outside of useMemo (effect below)
    pendingColorSync.current = localColorMap;

    return updatedAssets;
  }, [currentStart, rawProcesses, rawProcessEvents]);

  // Sync computed colors back to the module-level persistent cache (side effect, outside useMemo)
  useEffect(() => {
    if (pendingColorSync.current) {
      for (const [key, value] of pendingColorSync.current) {
        heatNameToColor.set(key, value);
      }
      pendingColorSync.current = null;
    }
  }, [assets]);

  return assets;
};
